import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  changeSecurityPassword,
  getSecurityMe,
  updateSecurityProfile,
} from "../../service/CallCenterSecurity";
import { getDefaultRouteFromPermissions } from "../../utils/permissions";

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";
const READONLY_CLASS =
  "w-full h-11 px-3 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-600";

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const profileInitialValues = {
  name: "",
  email: "",
};

const passwordInitialValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const initialsFromName = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const CallCenterProfilePage = () => {
  const t = useCallCenterT();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileInitial, setProfileInitial] = useState(profileInitialValues);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSecurityMe();
      setProfile(data);
      const nextValues = {
        name: data?.name || "",
        email: data?.email || "",
      };
      setProfileInitial(nextValues);
    } catch (error) {
      toast.error(t("ccProfileLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const profileSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("ccNameRequired")),
        email: Yup.string().trim().email(t("ccInvalidEmail")).required(t("ccEmailRequired")),
      }),
    [t]
  );

  const passwordSchema = useMemo(
    () =>
      Yup.object({
        currentPassword: Yup.string().required(t("ccCurrentPasswordRequired")),
        newPassword: Yup.string()
          .required(t("ccNewPasswordRequired"))
          .matches(PASSWORD_PATTERN, t("ccPasswordComplexity")),
        confirmPassword: Yup.string()
          .required(t("ccConfirmPasswordRequired"))
          .oneOf([Yup.ref("newPassword")], t("ccPasswordsMustMatch")),
      }),
    [t]
  );

  const handleBack = () => {
    const previousPath = Cookies.get("previousPath");
    navigate(previousPath || getDefaultRouteFromPermissions());
  };

  const handleProfileSubmit = async (values, { resetForm }) => {
    setSavingProfile(true);
    try {
      const updated = await updateSecurityProfile({
        name: values.name.trim(),
        email: values.email.trim(),
      });
      setProfile(updated);
      const nextValues = {
        name: updated?.name || "",
        email: updated?.email || "",
      };
      setProfileInitial(nextValues);
      resetForm({ values: nextValues });
      Cookies.set("userName", updated?.name || "");
      Cookies.set("userEmail", updated?.email || "");
      toast.success(t("ccProfileUpdated"));
    } catch (error) {
      toast.error(error?.message || t("ccProfileUpdateFailed"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (values, { resetForm }) => {
    setSavingPassword(true);
    try {
      await changeSecurityPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      resetForm({ values: passwordInitialValues });
      toast.success(t("ccPasswordChanged"));
    } catch (error) {
      toast.error(error?.message || t("ccPasswordChangeFailed"));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
          {t("ccLoading")}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <button
        type="button"
        onClick={handleBack}
        className="mb-4 text-sm font-medium text-[#2B7FFF] transition hover:text-[#1f6fe0]"
      >
        {t("ccBack")}
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">{t("ccProfileTitle")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("ccProfileSubtitle")}</p>
      </div>

      <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2B7FFF]/10 text-xl font-semibold text-[#2B7FFF]">
              {initialsFromName(profile?.name)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-slate-900">{profile?.name || "—"}</p>
              <p className="text-sm text-slate-500">{profile?.roleDisplayName || profile?.role || "—"}</p>
              {profile?.organizationName ? (
                <p className="text-xs text-slate-400">{profile.organizationName}</p>
              ) : null}
            </div>
          </div>
        </div>

        <Formik
          enableReinitialize
          initialValues={profileInitial}
          validationSchema={profileSchema}
          onSubmit={handleProfileSubmit}
        >
          {({ errors, touched, dirty, isSubmitting }) => (
            <Form className="space-y-5 px-6 py-6">
              <div>
                <label htmlFor="profile-name" className={LABEL_CLASS}>
                  {t("ccProfileName")}
                </label>
                <Field id="profile-name" name="name" className={FIELD_CLASS} autoComplete="name" />
                {touched.name && errors.name ? (
                  <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="profile-email" className={LABEL_CLASS}>
                  {t("ccProfileEmail")}
                </label>
                <Field id="profile-email" name="email" type="email" className={FIELD_CLASS} autoComplete="email" />
                {touched.email && errors.email ? (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                ) : null}
              </div>

              <div>
                <span className={LABEL_CLASS}>{t("ccProfileRole")}</span>
                <div className={READONLY_CLASS}>{profile?.roleDisplayName || profile?.role || "—"}</div>
              </div>

              <div>
                <span className={LABEL_CLASS}>{t("ccProfileOrganization")}</span>
                <div className={READONLY_CLASS}>{profile?.organizationName || "—"}</div>
              </div>

              {profile?.agentExtension ? (
                <div>
                  <span className={LABEL_CLASS}>{t("ccProfileAgentExtension")}</span>
                  <div className={READONLY_CLASS}>{profile.agentExtension}</div>
                </div>
              ) : null}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={!dirty || savingProfile || isSubmitting}
                  className="inline-flex h-11 items-center rounded-xl bg-[#2B7FFF] px-5 text-sm font-medium text-white transition hover:bg-[#1f6fe0] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingProfile ? t("ccSaving") : t("ccProfileSave")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">{t("ccProfileChangePassword")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("ccProfileChangePasswordHint")}</p>
        </div>

        <Formik
          initialValues={passwordInitialValues}
          validationSchema={passwordSchema}
          onSubmit={handlePasswordSubmit}
        >
          {({ errors, touched }) => (
            <Form className="space-y-5 px-6 py-6">
              <div>
                <label htmlFor="current-password" className={LABEL_CLASS}>
                  {t("ccCurrentPassword")}
                </label>
                <div className="relative">
                  <Field
                    id="current-password"
                    name="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    className={`${FIELD_CLASS} pr-11`}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                    onClick={() => setShowCurrentPassword((value) => !value)}
                    aria-label={showCurrentPassword ? t("ccHidePassword") : t("ccShowPassword")}
                  >
                    {showCurrentPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                  </button>
                </div>
                {touched.currentPassword && errors.currentPassword ? (
                  <p className="mt-1 text-xs text-red-500">{errors.currentPassword}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="new-password" className={LABEL_CLASS}>
                  {t("ccNewPassword")}
                </label>
                <div className="relative">
                  <Field
                    id="new-password"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    className={`${FIELD_CLASS} pr-11`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                    onClick={() => setShowNewPassword((value) => !value)}
                    aria-label={showNewPassword ? t("ccHidePassword") : t("ccShowPassword")}
                  >
                    {showNewPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                  </button>
                </div>
                {touched.newPassword && errors.newPassword ? (
                  <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="confirm-password" className={LABEL_CLASS}>
                  {t("ccConfirmPassword")}
                </label>
                <div className="relative">
                  <Field
                    id="confirm-password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className={`${FIELD_CLASS} pr-11`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                    onClick={() => setShowConfirmPassword((value) => !value)}
                    aria-label={showConfirmPassword ? t("ccHidePassword") : t("ccShowPassword")}
                  >
                    {showConfirmPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                  </button>
                </div>
                {touched.confirmPassword && errors.confirmPassword ? (
                  <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                ) : null}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="inline-flex h-11 items-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingPassword ? t("ccSaving") : t("ccProfileChangePassword")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CallCenterProfilePage;
